import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingManagementService, PlatformBooking, ListBookingsParams } from '../services/bookingManagement';
import { Table, TableColumn } from '../components/Table';
import './BookingsPage.css';

export const BookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<PlatformBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [customerIdFilter, setCustomerIdFilter] = useState('');
  const [providerIdFilter, setProviderIdFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const itemsPerPage = 50;

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListBookingsParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        customerId: customerIdFilter || undefined,
        providerId: providerIdFilter || undefined,
        startDate: startDateFilter || undefined,
        endDate: endDateFilter || undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await bookingManagementService.listBookings(params);
      setBookings(response.bookings);
      setTotalCount(response.count);
      setLastEvaluatedKey(
        response.lastEvaluatedKey
          ? encodeURIComponent(JSON.stringify(response.lastEvaluatedKey))
          : undefined
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- lastEvaluatedKey intentionally omitted to prevent refetch loop
  }, [currentPage, searchTerm, statusFilter, customerIdFilter, providerIdFilter, startDateFilter, endDateFilter]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadBookings();
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCustomerIdFilter('');
    setProviderIdFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleExport = () => {
    const csvContent = [
      ['Booking ID', 'Customer ID', 'Provider ID', 'Status', 'Scheduled Date', 'Scheduled Time', 'Amount', 'Payment Status', 'Created'].join(','),
      ...bookings.map(b => [
        b.bookingId,
        b.customerId,
        b.providerId,
        b.status,
        b.scheduledDate,
        b.scheduledTime,
        b.pricing.rate,
        b.paymentStatus,
        b.createdAt,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleViewBooking = useCallback((bookingId: string) => {
    navigate(`/dashboard/bookings/${bookingId}`);
  }, [navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = useCallback((dateString: string, timeString: string) => {
    return `${formatDate(dateString)} at ${timeString}`;
  }, []);

  const columns: TableColumn<PlatformBooking>[] = useMemo(() => [
    {
      header: 'Booking ID',
      accessor: 'bookingId',
      render: (value) => (
        <div className="booking-id">{(value as string).substring(0, 8)}...</div>
      ),
    },
    {
      header: 'Customer',
      accessor: 'customerId',
      render: (value) => (
        <div className="secondary-text">{(value as string).substring(0, 8)}...</div>
      ),
      hideOnTablet: true,
    },
    {
      header: 'Provider',
      accessor: 'providerId',
      render: (value) => (
        <div className="secondary-text">{(value as string).substring(0, 8)}...</div>
      ),
      hideOnTablet: true,
    },
    {
      header: 'Scheduled',
      accessor: (row) => formatDateTime(row.scheduledDate, row.scheduledTime),
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => (
        <span className={`status-badge ${value as string}`}>
          {value as string}
        </span>
      ),
    },
    {
      header: 'Payment',
      accessor: 'paymentStatus',
      render: (value) => (
        <span className={`payment-status-badge ${value as string}`}>
          {value as string}
        </span>
      ),
      hideOnTablet: true,
    },
    {
      header: 'Amount',
      accessor: (row) => `₹${row.pricing.rate.toLocaleString()}`,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
      hideOnMobile: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <button
          className="view-button"
          onClick={() => handleViewBooking(row.bookingId)}
        >
          View
        </button>
      ),
      dataLabel: '',
    },
  ], [formatDateTime, handleViewBooking]);

  return (
    <div className="bookings-page">
      <div className="bookings-header">
        <h1>Booking Management</h1>
        <div className="bookings-stats">
          <div className="stat-card">
            <div className="stat-value">{totalCount}</div>
            <div className="stat-label">Total Bookings</div>
          </div>
          <button onClick={handleExport} className="export-button">
            Export CSV
          </button>
        </div>
      </div>

      <div className="bookings-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by booking ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            Search
          </button>
        </form>

        <div className="advanced-filters">
          <input
            type="text"
            placeholder="Customer ID"
            value={customerIdFilter}
            onChange={(e) => {
              setCustomerIdFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-input"
          />
          <input
            type="text"
            placeholder="Provider ID"
            value={providerIdFilter}
            onChange={(e) => {
              setProviderIdFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-input"
          />
          <input
            type="date"
            placeholder="Start Date"
            value={startDateFilter}
            onChange={(e) => {
              setStartDateFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-input"
          />
          <input
            type="date"
            placeholder="End Date"
            value={endDateFilter}
            onChange={(e) => {
              setEndDateFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-input"
          />
          <button onClick={handleClearFilters} className="clear-filters-button">
            Clear Filters
          </button>
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-button ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-button ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`filter-button ${statusFilter === 'confirmed' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('confirmed')}
          >
            Confirmed
          </button>
          <button
            className={`filter-button ${statusFilter === 'paid' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('paid')}
          >
            Paid
          </button>
          <button
            className={`filter-button ${statusFilter === 'in_progress' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('in_progress')}
          >
            In Progress
          </button>
          <button
            className={`filter-button ${statusFilter === 'completed' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('completed')}
          >
            Completed
          </button>
          <button
            className={`filter-button ${statusFilter === 'cancelled' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('cancelled')}
          >
            Cancelled
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading bookings...</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={bookings}
            getRowId={(row) => row.bookingId}
            emptyMessage="No bookings found"
            containerClassName="bookings-table-container"
            className="bookings-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || bookings.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

